import { useForm } from "@inertiajs/react"
import Button from "../Button"
import { AdminUser } from "@/types/serializers"
import { useState } from "react"
import ErrorMessage from "../ErrorMessage"

const options: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  timeZoneName: "short",
}

type Inputs = {
  fide_id: string
  unverified_fide_id: string
  email: string
}

const formatter = new Intl.DateTimeFormat("en-US", options)

const EditUserForm = ({ user }: { user: AdminUser }) => {
  const [showForm, setShowForm] = useState<"edit" | "delete" | null>(null)

  const isEditing = showForm === "edit"
  const isDeleting = showForm === "delete"

  const { data, setData, patch, processing, errors } = useForm<Inputs>({
    fide_id: user.fide_id || "",
    unverified_fide_id: user.unverified_fide_id || "",
    email: user.email || "",
  })

  const {
    data: deleteData,
    setData: setDeleteData,
    delete: destroy,
    processing: deleteProcessing,
    errors: deleteErrors,
    resetAndClearErrors,
  } = useForm({
    email: "",
  })

  const saveUser = (e: React.FormEvent) => {
    e.preventDefault()

    patch(`/admin/users/${user.id}`, {
      async: true,
      preserveScroll: true,
      preserveState: true,
      preserveUrl: true,
      onSuccess: () => setShowForm(null),
    })
  }

  const isConfirmed = deleteData.email === user.email

  const deleteUser = (e: React.FormEvent) => {
    e.preventDefault()

    if (!isConfirmed) return

    destroy(`/admin/users/${user.id}`, {
      async: true,
      preserveScroll: true,
      preserveState: true,
      preserveUrl: true,
      onSuccess: () => {
        resetAndClearErrors()
        setShowForm(null)
      },
    })
  }

  const Tag = showForm === "edit" ? "form" : "div"

  return (
    <Tag
      key={user.email}
      onSubmit={isEditing ? saveUser : undefined}
      className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow"
    >
      <div className="px-4 pt-5 pb-6 sm:px-6">
        <dl className="grid gap-5">
          <div>
            <dt className="text-sm leading-6 font-semibold text-gray-900">
              Email
            </dt>
            {isEditing ? (
              <>
                <input
                  type="email"
                  id="email"
                  className="focus:ring-chessdrop-600 hide-spinners block w-full rounded-xl border-0 p-3 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
                  value={data.email}
                  onChange={(e) => setData("email", e.target.value)}
                />
                <ErrorMessage className="mt-1.5" message={errors.email} />
              </>
            ) : (
              <dd className="text-sm leading-6 text-gray-600">{user.email}</dd>
            )}
          </div>

          <div>
            <dt className="text-sm leading-6 font-semibold text-gray-900">
              Name
            </dt>
            <dd className="text-sm leading-6 text-gray-600">
              {user.name || "N/A"}
            </dd>
          </div>

          <div>
            <dt className="text-sm leading-6 font-semibold text-gray-900">
              Created at
            </dt>
            <dd className="text-sm leading-6 text-gray-600">
              {formatter.format(new Date(user.created_at))}
            </dd>
          </div>

          <div>
            <dt className="text-sm leading-6 font-semibold text-gray-900">
              FIDE ID
            </dt>
            <dd className="text-sm leading-6 text-gray-600">
              {isEditing ? (
                <>
                  <input
                    type="number"
                    inputMode="numeric"
                    id="fide_id"
                    className="focus:ring-chessdrop-600 hide-spinners block w-full rounded-xl border-0 p-3 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
                    value={data.fide_id}
                    onChange={(e) => setData("fide_id", e.target.value)}
                  />
                  <ErrorMessage className="mt-1.5" message={errors.fide_id} />
                </>
              ) : (
                user.fide_id || "N/A"
              )}
            </dd>
          </div>

          <div>
            <dt className="text-sm leading-6 font-semibold text-gray-900">
              FIDE ID unverified
            </dt>
            <dd className="text-sm leading-6 text-gray-600">
              {isEditing ? (
                <>
                  <input
                    type="number"
                    inputMode="numeric"
                    id="unverified_fide_id"
                    className="focus:ring-chessdrop-600 hide-spinners block w-full rounded-xl border-0 p-3 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
                    value={data.unverified_fide_id}
                    onChange={(e) =>
                      setData("unverified_fide_id", e.target.value)
                    }
                  />
                  <ErrorMessage
                    className="mt-1.5"
                    message={errors.unverified_fide_id}
                  />
                </>
              ) : (
                user.unverified_fide_id || "N/A"
              )}
            </dd>
          </div>
        </dl>
      </div>

      <div className="border-t border-gray-200 px-4 py-4 sm:px-6">
        <div className="flex items-center justify-end gap-x-3">
          {isEditing ? (
            <div className="flex gap-x-3">
              <Button
                type="button"
                style="secondary"
                onClick={() => setShowForm(null)}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button type="submit" style="primary" loading={processing}>
                Save
              </Button>
            </div>
          ) : isDeleting ? (
            <form onSubmit={deleteUser} className="space-y-5">
              <p className="text-sm text-gray-600">
                Deletion of the user will <b>irrevocably</b> delete all data
                associated with the account. Please type <b>{user.email}</b>{" "}
                into the field below, and confirm you wish to delete the user
                account.
              </p>

              <div>
                <label htmlFor="email" className="sr-only">
                  Email address
                </label>

                <input
                  type="email"
                  id="email"
                  placeholder="Enter user’s email address"
                  autoComplete="off"
                  required
                  className="focus:ring-chessdrop-600 block w-full rounded-xl border-0 p-3 text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
                  value={deleteData.email}
                  onChange={(e) => setDeleteData("email", e.target.value)}
                />

                <ErrorMessage className="mt-1.5" message={deleteErrors.email} />
              </div>

              <div className="flex items-center justify-end gap-x-3">
                <Button
                  type="button"
                  style="secondary"
                  onClick={() => {
                    resetAndClearErrors()
                    setShowForm(null)
                  }}
                  disabled={deleteProcessing}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  style="destructive"
                  disabled={!isConfirmed || processing}
                  loading={deleteProcessing}
                >
                  Delete user
                </Button>
              </div>
            </form>
          ) : (
            <div className="flex flex-1 justify-between gap-x-3">
              <Button
                type="button"
                style="destructive"
                onClick={() => setShowForm("delete")}
              >
                Delete user
              </Button>
              <Button
                type="button"
                style="secondary"
                onClick={() => setShowForm("edit")}
              >
                Edit user
              </Button>
            </div>
          )}
        </div>
      </div>
    </Tag>
  )
}

export default EditUserForm
